import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/UI';
import styles from './AdminSignup.module.css';

export default function AdminSignup() {
  const { signup } = useAuth();
  const navigate   = useNavigate();

  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [showPw, setShowPw]       = useState(false);
  const [showCf, setShowCf]       = useState(false);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);

  const validate = () => {
    if (!email.trim()) return 'Email is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid email address.';
    if (password.length < 8) return 'Password must be at least 8 characters.';
    if (!/[A-Z]/.test(password)) return 'Password must include at least one uppercase letter.';
    if (!/[0-9]/.test(password)) return 'Password must include at least one number.';
    if (password !== confirm) return 'Passwords do not match.';
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setLoading(true);
    try {
      await signup(email, password);
      navigate('/admin');
    } catch (err) {
      setError(
        err.code === 'auth/email-already-in-use'
          ? 'An account with this email already exists.'
          : 'Sign-up failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  /* Password strength */
  const strength = (() => {
    if (!password) return null;
    let score = 0;
    if (password.length >= 8)          score++;
    if (password.length >= 12)         score++;
    if (/[A-Z]/.test(password))        score++;
    if (/[0-9]/.test(password))        score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    if (score <= 2) return { label: 'Weak',   color: '#e74c3c', width: '33%'  };
    if (score <= 3) return { label: 'Fair',   color: '#f39c12', width: '60%'  };
    return            { label: 'Strong', color: '#27ae60', width: '100%' };
  })();

  const requirements = [
    { label: 'At least 8 characters',  met: password.length >= 8 },
    { label: 'One uppercase letter',   met: /[A-Z]/.test(password) },
    { label: 'One number',             met: /[0-9]/.test(password) },
    { label: 'Passwords match',        met: password.length > 0 && password === confirm },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logoArea}>
          <img src="/logo.png" alt="FEwB" className={styles.logoImg} />
          <h1 className={styles.title}>Create Admin Account</h1>
          <p className={styles.subtitle}>Field Epidemiologists without Borders</p>
        </div>

        <form onSubmit={submit} className={styles.form}>

          {/* Email */}
          <div className={styles.field}>
            <label className={styles.label}>Email Address</label>
            <input
              className={styles.input}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="admin@fewb.org"
              required
            />
          </div>

          {/* Password */}
          <div className={styles.field}>
            <label className={styles.label}>Password</label>
            <div className={styles.pwWrap}>
              <input
                className={styles.input}
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Min. 8 chars, 1 uppercase, 1 number"
                required
              />
              <button
                type="button"
                className={styles.eyeBtn}
                onClick={() => setShowPw(v => !v)}
                aria-label={showPw ? 'Hide password' : 'Show password'}
              >
                {showPw ? <EyeOff /> : <EyeOn />}
              </button>
            </div>
            {/* Strength bar */}
            {strength && (
              <div className={styles.strengthWrap}>
                <div className={styles.strengthBar}>
                  <div
                    className={styles.strengthFill}
                    style={{ width: strength.width, background: strength.color }}
                  />
                </div>
                <span className={styles.strengthLabel} style={{ color: strength.color }}>
                  {strength.label}
                </span>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className={styles.field}>
            <label className={styles.label}>Confirm Password</label>
            <div className={styles.pwWrap}>
              <input
                className={styles.input}
                type={showCf ? 'text' : 'password'}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="Re-enter your password"
                required
              />
              <button
                type="button"
                className={styles.eyeBtn}
                onClick={() => setShowCf(v => !v)}
                aria-label={showCf ? 'Hide password' : 'Show password'}
              >
                {showCf ? <EyeOff /> : <EyeOn />}
              </button>
            </div>
          </div>

          {error && <p className={styles.error}>{error}</p>}

          {/* Requirements checklist */}
          <div className={styles.requirements}>
            <p className={styles.reqTitle}>Password requirements</p>
            <ul className={styles.reqList}>
              {requirements.map(r => (
                <li key={r.label} className={`${styles.reqItem} ${r.met ? styles.reqMet : ''}`}>
                  <span className={styles.reqDot}>{r.met ? '✓' : '○'}</span>
                  {r.label}
                </li>
              ))}
            </ul>
          </div>

          <Button
            type="submit"
            variant="primary"
            loading={loading}
            style={{ width: '100%', justifyContent: 'center' }}
          >
            Create Account
          </Button>
        </form>

        <div className={styles.footer}>
          <p className={styles.footerText}>
            Already have an account?{' '}
            <Link to="/admin/login" className={styles.footerLink}>Sign in</Link>
          </p>
          <a href="/" className={styles.backLink}>← Back to Questionnaire</a>
        </div>
      </div>
    </div>
  );
}

/* ── Inline SVG eye icons ── */
function EyeOn() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

function EyeOff() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}